// src/services/retryService.js
//
// "Retry my mistakes": the questions a student missed in an exam, asked again after
// a pause (spacing). One set per saved attempt at users/{uid}/retrySets/{examResultId}.
//  - The set opens 2 days after the exam.
//  - A question answered right in a retry is done; one missed again comes back 7 days later.
//  - Free users get the set too (shown locked), so it is ready the moment they upgrade.
// Reminders: functions/src/retryReminders.ts mirrors open sets into retryReminders/ and
// emails the student on the due day; the dashboard shows a card while a set is due.
import {
  collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, Timestamp, updateDoc, where,
} from 'firebase/firestore';
import { db } from '../firebase';

const DAY = 24 * 60 * 60 * 1000;
export const FIRST_RETRY_DAYS = 2;
export const AGAIN_RETRY_DAYS = 7;
// Only fresh attempts get a set: older ones may be on an earlier version of the paper.
const MAX_ATTEMPT_AGE_DAYS = 3;

const setRef = (uid, setId) => doc(db, 'users', uid, 'retrySets', setId);
const toMs = (v) => (v?.toMillis ? v.toMillis() : v instanceof Date ? v.getTime() : Number(v) || 0);

/** Earliest due time of the questions still open, or null when all are done. */
const nextDue = (items) => {
  const open = items.filter((it) => !it.done).map((it) => it.dueAt);
  return open.length ? Math.min(...open) : null;
};

/**
 * Creates the retry set for a just-finished attempt (once; safe to call on every render
 * of the results page). `review` is resultInsights.review: wrong items, confident first.
 */
// Typed answers (notes, gap words, table cells) cannot be re-asked as a choice, so they stay out.
const TYPED_TASKS = new Set(['lecture', 'information-transfer-notes', 'information-transfer', 'text-reconstruction-endings', 'cloze-free-text']);

export async function ensureRetrySet(uid, { examResultId, exam, review: allWrong, completedAt }) {
  const typed = new Set((exam?.sections || []).flatMap((s) => s.parts || []).filter((p) => TYPED_TASKS.has(p.taskType)).map((p) => p.partId));
  const review = (allWrong || []).filter((it) => !typed.has(it.partId));
  if (!uid || !examResultId || !exam?.examFileName || !review.length) return null;
  const done = toMs(completedAt) || Date.now();
  if (Date.now() - done > MAX_ATTEMPT_AGE_DAYS * DAY) return null;
  const ref = setRef(uid, examResultId);
  const snap = await getDoc(ref);
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  const dueAt = done + FIRST_RETRY_DAYS * DAY;
  const data = {
    examResultId,
    provider: String(exam.examProvider || '').toLowerCase(),
    level: String(exam.level || '').toLowerCase(),
    examFileName: exam.examFileName,
    examName: exam.variantName || exam.examName || '',
    createdAt: serverTimestamp(),
    dueAt: Timestamp.fromMillis(dueAt),
    status: 'open',
    total: review.length,
    items: review.map((it) => ({
      questionId: it.questionId,
      partId: it.partId,
      section: it.section,
      number: it.number ?? null,
      confident: !!it.confident,
      dueAt,
      done: false,
      tries: [],
    })),
  };
  await setDoc(ref, data);
  return { id: examResultId, ...data, dueAt: data.dueAt };
}

export async function getRetrySet(uid, setId) {
  const snap = await getDoc(setRef(uid, setId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** The student's open sets, soonest first, with how many questions are due now. */
export async function listOpenRetrySets(uid) {
  if (!uid) return [];
  const snap = await getDocs(query(collection(db, 'users', uid, 'retrySets'), where('status', '==', 'open')));
  const now = Date.now();
  return snap.docs
    .map((d) => {
      const data = d.data();
      const items = data.items || [];
      return {
        id: d.id,
        ...data,
        dueAtMs: toMs(data.dueAt),
        dueNow: items.filter((it) => !it.done && it.dueAt <= now).length,
        remaining: items.filter((it) => !it.done).length,
      };
    })
    .sort((a, b) => a.dueAtMs - b.dueAtMs);
}

/** Questions to ask now: open and due, in the stored order (confident mistakes first). */
export const dueItems = (set, now = Date.now()) => (set?.items || []).filter((it) => !it.done && it.dueAt <= now);

/**
 * Saves one retry answer. Right → done; wrong → back in 7 days.
 * Returns the updated set (items, dueAt, status).
 */
export async function recordRetryAnswer(uid, set, key, correct) {   // key: partId/questionId
  const now = Date.now();
  const items = (set.items || []).map((it) => (`${it.partId}/${it.questionId}` !== key ? it : {
    ...it,
    done: !!correct,
    dueAt: correct ? it.dueAt : now + AGAIN_RETRY_DAYS * DAY,
    tries: [...(it.tries || []), { at: now, correct: !!correct }],
  }));
  const due = nextDue(items);
  const patch = { items, status: due == null ? 'done' : 'open', dueAt: due == null ? set.dueAt : Timestamp.fromMillis(due), updatedAt: serverTimestamp() };
  await updateDoc(setRef(uid, set.id), patch);
  return { ...set, ...patch };
}
